import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SettlementMode } from "@/services/b2b";
import { partnerArApi, type CreditOverview } from "@/services/partnerAr";
import { formatCents } from "@/services/finance";
import { Can, useCan } from "@/auth/permissions";

/**
 * 同行详情「信用 & 结算」Tab 顶部 — 样稿 credit-mockup-v1 ①。
 * 额度口径唯一来自后端 GET /api/v1/channel-partners/{id}/credit（授信 = 基础 + 生效中的临时；已用 = 未收应收 + 待确认占位）。
 * 结算条件留空 = 用全局默认（全局默认在「系统设置 · 通用设置」）。
 */
const MODE_LABEL: Record<SettlementMode, string> = { credit_monthly: "月结", credit_per_trip: "单结", prepay: "预付" };

export const invalidateCredit = (qc: ReturnType<typeof useQueryClient>, partnerId: string) => {
  for (const key of ["credit", "partner", "partner-stats", "receivables", "payments", "statements", "board", "settlement-board", "outstanding"]) {
    qc.invalidateQueries({ queryKey: ["b2b", key] });
  }
  qc.invalidateQueries({ queryKey: ["b2b", "credit", partnerId] });
};

export function PartnerCreditOverview({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const canTerms = useCan("channel_partners.partners.update");
  const q = useQuery({ queryKey: ["b2b", "credit", partnerId], queryFn: () => partnerArApi.credit(partnerId) });
  const [unblockOpen, setUnblockOpen] = useState(false);

  if (q.isLoading) return <div className="rounded-xl border border-slate-200 bg-white p-6 text-[13px] text-slate-400">额度加载中…</div>;
  if (q.isError || !q.data) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-6 text-[13px] text-slate-500">
        额度加载失败 <button type="button" onClick={() => q.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
      </div>
    );
  }
  const c = q.data;
  const base = Math.max(c.limit_cents, 1);
  const usedPct = Math.min(100, (c.receivable_open_cents / base) * 100);
  const holdPct = Math.min(100 - usedPct, (c.hold_cents / base) * 100);
  const tempTotal = c.temp_limits.reduce((a, t) => a + t.amount_cents, 0);
  const tempSub = c.temp_limits.length
    ? `基础 ${formatCents(c.base_limit_cents)} + 临时 ${formatCents(tempTotal)}（${c.temp_limits.map((t) => t.effective_to.slice(5)).join("、")} 到期自动收回）`
    : `基础 ${formatCents(c.base_limit_cents)}（无生效中的临时额度）`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="credit-overview">
      {c.blocked && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800" data-testid="credit-blocked-bar">
          <span>⛔ 已停新单：逾期 {c.overdue_max_days} 天（{formatCents(c.overdue_cents)} · {c.overdue_count} 笔）。付清逾期款后自动恢复</span>
          <Can perm="channel_partners.quotas.approve">
            <button type="button" onClick={() => setUnblockOpen(true)} className="ml-auto h-7 rounded-lg border border-rose-300 bg-white px-3 text-[12px] font-medium text-rose-600 hover:bg-rose-50">老板手动解锁</button>
          </Can>
        </div>
      )}
      {!c.blocked && c.unblocked_until && (
        <div className="mb-3 rounded-[10px] border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-sky-800" data-testid="credit-unblocked-bar">
          已手动解锁至 {c.unblocked_until.slice(0, 10)}：期间照常接单；到期仍有逾期会重新停单。
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-5">
        <Kpi label="授信额度" value={formatCents(c.limit_cents)} sub={tempSub} />
        <Kpi label="已用" value={formatCents(c.used_cents)} sub={`未收应收 ${formatCents(c.receivable_open_cents)} + 待确认占位 ${formatCents(c.hold_cents)}`} />
        <Kpi label="可用" value={formatCents(c.available_cents)} sub="停单期间可用视为 0" color="#059669" />
        <Kpi label="逾期" value={formatCents(c.overdue_cents)} sub={c.overdue_count ? `${c.overdue_count} 笔 · 最长 ${c.overdue_max_days} 天` : "无逾期"} color={c.overdue_cents > 0 ? "#E11D48" : undefined} />
        <Kpi label="预收余额" value={formatCents(c.prepaid_balance_cents)} sub="下次收款 / 应收自动抵扣" />
      </div>
      <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-slate-100" aria-label="额度占用">
        <span style={{ width: `${usedPct}%`, background: "#2563EB" }} />
        <span style={{ width: `${holdPct}%`, background: "#93C5FD" }} />
      </div>
      <div className="mt-1 text-[11.5px] text-slate-400">深蓝 = 未收应收 · 浅蓝 = 待确认占位 · 灰 = 可用</div>

      <TermsForm credit={c} partnerId={partnerId} canEdit={canTerms} />

      {unblockOpen && <UnblockDialog partnerId={partnerId} onClose={() => setUnblockOpen(false)} onDone={() => { setUnblockOpen(false); invalidateCredit(qc, partnerId); }} />}
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div className="rounded-[10px] border border-slate-100 bg-slate-50 px-3 py-2.5">
      <div className="text-[12px] text-slate-400">{label}</div>
      <div className="text-[19px] font-semibold tabular-nums" style={color ? { color } : undefined}>{value}</div>
      <div className="text-[11.5px] leading-snug text-slate-400">{sub}</div>
    </div>
  );
}

function TermsForm({ credit, partnerId, canEdit }: { credit: CreditOverview; partnerId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const t = credit.terms;
  const [mode, setMode] = useState<SettlementMode>(t.mode);
  const [monthly, setMonthly] = useState(t.monthly_due_day?.toString() ?? "");
  const [perOrder, setPerOrder] = useState(t.per_order_due_days?.toString() ?? "");
  const [block, setBlock] = useState(t.block_after_overdue_days?.toString() ?? "");
  useEffect(() => {
    setMode(t.mode);
    setMonthly(t.monthly_due_day?.toString() ?? "");
    setPerOrder(t.per_order_due_days?.toString() ?? "");
    setBlock(t.block_after_overdue_days?.toString() ?? "");
  }, [t.mode, t.monthly_due_day, t.per_order_due_days, t.block_after_overdue_days]);

  const toNum = (v: string): number | null => (v.trim() === "" ? null : Number(v));
  const bad = (v: string, lo: number, hi: number) => v.trim() !== "" && !(Number.isInteger(Number(v)) && Number(v) >= lo && Number(v) <= hi);
  const invalid = bad(monthly, 1, 28) || bad(perOrder, 0, 90) || bad(block, 0, 365);
  const dirty = mode !== t.mode || toNum(monthly) !== t.monthly_due_day || toNum(perOrder) !== t.per_order_due_days || toNum(block) !== t.block_after_overdue_days;
  const save = useMutation({
    mutationFn: () => partnerArApi.patchTerms(partnerId, { mode, monthly_due_day: toNum(monthly), per_order_due_days: toNum(perOrder), block_after_overdue_days: toNum(block) }),
    onSuccess: () => invalidateCredit(qc, partnerId),
    onError: (e) => alert((e as Error).message || "保存结算条件失败"),
  });
  const g = t.global_defaults;
  const inp = "h-9 w-[70px] rounded-lg border border-slate-200 px-2 text-[13px] disabled:bg-slate-50";
  return (
    <div className="mt-4" data-testid="terms-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">结算方式
          <select value={mode} disabled={!canEdit} onChange={(e) => setMode(e.target.value as SettlementMode)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[13px] disabled:bg-slate-50" aria-label="结算方式">
            {(Object.keys(MODE_LABEL) as SettlementMode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">月结到期日
          <span className="flex items-center gap-1.5 text-[13px] text-slate-700">次月 <input className={inp} inputMode="numeric" disabled={!canEdit} value={monthly} placeholder={String(g.monthly_due_day)} onChange={(e) => setMonthly(e.target.value)} aria-label="月结到期日" /> 号</span>
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">单结到期
          <span className="flex items-center gap-1.5 text-[13px] text-slate-700">出发后 <input className={inp} inputMode="numeric" disabled={!canEdit} value={perOrder} placeholder={String(g.per_order_due_days)} onChange={(e) => setPerOrder(e.target.value)} aria-label="单结到期天数" /> 天</span>
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">逾期自动停单
          <span className="flex items-center gap-1.5 text-[13px] text-slate-700">超过 <input className={inp} inputMode="numeric" disabled={!canEdit} value={block} placeholder={String(g.block_after_overdue_days)} onChange={(e) => setBlock(e.target.value)} aria-label="逾期停单天数" /> 天</span>
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11.5px] text-slate-400">
        <span>留空 = 用全局默认（次月 {g.monthly_due_day} 号 / 出发后 {g.per_order_due_days} 天 / {g.block_after_overdue_days} 天）；全局默认在「系统设置 · 通用设置 · 同行结算」里改 · 当前生效：次月 {t.effective.monthly_due_day} 号 / 出发后 {t.effective.per_order_due_days} 天 / {t.effective.block_after_overdue_days} 天。改了只影响之后新生成的应收。</span>
        {canEdit && (
          <button type="button" disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()} data-testid="terms-save"
            className="ml-auto h-8 rounded-lg bg-blue-600 px-4 text-[12px] font-semibold text-white disabled:opacity-40">
            {save.isPending ? "保存中…" : "保存结算条件"}
          </button>
        )}
      </div>
      {invalid && <div className="mt-1 text-[11.5px] text-rose-600">月结到期日 1–28、单结天数 0–90、停单天数 0–365，留空用全局默认</div>}
    </div>
  );
}

function UnblockDialog({ partnerId, onClose, onDone }: { partnerId: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => partnerArApi.unblock(partnerId, reason.trim()),
    onSuccess: onDone,
    onError: (e) => alert((e as Error).message || "解锁失败"),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/35 p-4 pt-[14vh]" onClick={onClose}>
      <div role="dialog" className="flex w-full max-w-[440px] flex-col gap-3 rounded-[14px] bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-[16px] font-semibold text-slate-900">手动解锁停单</div>
        <div className="text-[12.5px] text-slate-600">解锁后 7 天内照常接单（到期仍有逾期会重新停单）；原因会写进操作日志。</div>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="解锁原因（必填，如：对方已转账，凭证在途）" aria-label="解锁原因" className="min-h-[80px] rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-[13px] text-slate-600">取消</button>
          <button type="button" disabled={!reason.trim() || m.isPending} onClick={() => m.mutate()} className="h-9 rounded-lg bg-rose-600 px-4 text-[13px] font-semibold text-white disabled:opacity-40">{m.isPending ? "解锁中…" : "确认解锁"}</button>
        </div>
      </div>
    </div>
  );
}
